import { apiDivisions } from "@/api/division.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiDivisionFilter } from "@/types/division.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

const divisions = createQueryKeys("divisions", {
  search: (filter: ApiDivisionFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiDivisions.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byListOfId: (ids: number[]) => ({
    queryKey: [ids],
    queryFn: async () => {
      const result = await apiDivisions.getByListOfId(ids);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useDivisionsSearch = (filter: ApiDivisionFilter, enabled = true) => {
  return useQuery({
    ...divisions.search(filter),
    enabled: enabled,
  });
};

export interface DivisionSaveParams {
  id: number;
  title: string;
}

export const useDivisionSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, title }: DivisionSaveParams) => {
      const result =
        id == 0 ? await apiDivisions.create(title) : await apiDivisions.update(id, title);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: divisions.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useDivisionDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiDivisions.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: divisions.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDivisionsSearchInline = (enabled: boolean = true) => {
  const [filter, setFilter] = useState<ApiDivisionFilter>({
    page_number: 1,
    text: undefined,
  });
  const { data, isLoading } = useDivisionsSearch(filter, enabled);
  const options = useMemo(() => {
    return (data?.items || []) as SelectItem[];
  }, [data]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text: text });
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useDivisionsByIds = ({
  ids,
  enabled = true,
}: {
  ids: number[];
  enabled?: boolean;
}) => {
  return useQuery({
    ...divisions.byListOfId(ids),
    enabled: enabled && ids.length > 0,
  });
};
